<template>
<v-layout v-if="harvestPlan" column>
    <v-card flat>
        <v-toolbar dense color="shrink grey darken-1" card class="white--text">
            <v-toolbar-title>Summary</v-toolbar-title>
        </v-toolbar>
    </v-card> 
    <v-card>
        <v-layout row wrap pa-2>
            <v-flex xs12>
                <v-alert type="warning" outline :value="isPastMidnight">Warning - Estimated Finish Time extends after midnight.</v-alert>
            </v-flex>
            <v-flex>
                <label-value :label="'Volume'" :value="`${formatVolume(getTeamVolume(null)) || 'N/A'} ${selectedUOM == $Constants.HARVESTPLAN_UOM_ENUM.KG.ID ? '(Kgs)' : '(Trays)'}`"></label-value>
            </v-flex>
            <v-flex>
                <label-value :label="'ICA'" :value="`${formatVolume(getTeamICAVolume(null)) || 'N/A'} ${selectedUOM == $Constants.HARVESTPLAN_UOM_ENUM.KG.ID ? '(Kgs)' : '(Trays)'}`"></label-value>
            </v-flex>
            <v-flex>
                <label-value :label="'Avg Pick Rate'" :value="`${getTeamPickRate(null)} ${selectedUOM == $Constants.HARVESTPLAN_UOM_ENUM.KG.ID ? '(Derived Kgs/Hr)' : '(Derived Pun/Hr)'}`"></label-value>
            </v-flex>
            <v-flex>
                <label-value :label="'Start Time'" :value="formatTime(startTime)"></label-value>
            </v-flex>
            <v-flex>
                <label-value :label="'Est. Finish Time'" :value="formatTime(endTime)"></label-value>
            </v-flex>
            <v-flex>
                <label-value :label="'Total Pickers'" :value="totalPickers"></label-value>
            </v-flex>
            <v-flex>
                <label-value :label="`Total ${getCurrencySymbol}/Kg`" :value="`${getCurrencySymbol}${costPerKg()}`"></label-value>
            </v-flex>
            <v-flex>
                <label-value :label="'Comments'" :value="totalComments" :editable="true" :editOnEnter="true">
                    <template v-slot:edit="{ commit }">
                        <label-value :label="'Comments'" :value="totalComments" :editable="true"></label-value>
                        <HarvestPlanCommentsDialog :editable="!isPast" :safety="harvestPlan.SafetyComments" :quality="harvestPlan.QualityComments" :general="harvestPlan.GeneralComments" @cancel="commit" @save="handleSaveComments($event, commit)"></HarvestPlanCommentsDialog>
                    </template>
                </label-value>
            </v-flex>
        </v-layout>
    </v-card>
</v-layout>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex";
import LabelValue from '@/components/generic/LabelValue.vue';
import UtilService from '@/services/utilService.js';
import moment from 'moment';
import * as Constants from "@/helpers/constants";
import HarvestPlanCommentsDialog from '@/components/harvestPlan/HarvestPlanCommentsDialog.vue'

export default {
    components: {
        LabelValue,
        HarvestPlanCommentsDialog
    },    
    data() {
        return{
            moment
        }
    },
    computed: {
        ...mapState("HarvestPlanStore", [            
            "harvestPlan",
            "plantData"
        ]),
        ...mapGetters("HarvestPlanStore", [
            "getTeamVolume",
            "getTeamICAVolume",
            "getTeamVolumeKg",
            "getTeamPickRate",            
            "getTeamCost",
            "getTeamStartTime",
            "getTeamFinishTime",
            "getTeams",
            "isPast",
            "selectedUOM"
        ]),
        totalPickers() {
            let teams = this.getTeams;

            if(teams && teams.length > 0) {
                return teams.map(t => parseInt(t.PickerStaff || 0)).reduce((a, b) => a + b) || 'N/A';
            }

            return 'N/A';
        },
        isPastMidnight() {
            if (this.startTime && this.endTime) {
                return this.endTime.date() > this.startTime.date();
            }

            return false;
        },
        startTime() {
            let start = null;
            this.getTeams.forEach(team => {
                if (team.StartTime) {
                    let teamStartTime = this.getTeamStartTime(team.BerriesHarvestPlanTeamID);
                    if (!start) {
                        start = teamStartTime;
                    }

                    if (teamStartTime.isBefore(start)) {
                        start = teamStartTime;
                    }
                }
            });

            return start;
        },
        endTime() {
            let end = null;
            this.getTeams.forEach(team => {
                let endTime = this.getTeamFinishTime(team.BerriesHarvestPlanTeamID);
                if (endTime) {                    
                    if (!end) {
                        end = endTime;
                    }

                    if (endTime.isAfter(end)) {
                        end = endTime;
                    }
                }
            });

            return end;
        },
        totalComments() {
            let total = 0;

            if (this.harvestPlan) {
                if (this.harvestPlan.SafetyComments && this.harvestPlan.SafetyComments.length > 0) {
                    total += 1;
                }

                if (this.harvestPlan.QualityComments && this.harvestPlan.QualityComments.length > 0) {
                    total += 1;
                }

                if (this.harvestPlan.GeneralComments && this.harvestPlan.GeneralComments.length > 0) {
                    total += 1;
                }
            }

            return total;
        },
        getCurrencySymbol() {
            var selectedPlantData = this.plantData.find(x => x.PlantID == this.harvestPlan.PlantID)

            if(Constants.COMPANIES.CHINA.CompanyID == selectedPlantData.CompanyID){
                return '¥'
            }
            return '$'
        }
    },
    methods: {
        ...mapActions("HarvestPlanStore", [
            "updateHarvestPlanDetail"
        ]),
        formatTime(v) {
            if (v) {
                return v.format("HH:mm");
            }

            return 'N/A'
        },
        formatVolume(v) {                    
            if (v !== null && v !== Infinity) {
                return UtilService.thousandsSeparators(v);
            }

            return null;
        },
        costPerKg() {
            let teamVolumeKg = this.getTeamVolumeKg(null);
            let teamCost = this.getTeamCost(null);

            if (teamVolumeKg > 0 && teamCost >= 0) {
                return this.formatVolume(Math.round(teamCost / teamVolumeKg * 100) / 100) || 'N/A';
            }

            return 'N/A';
        },
        async handleSaveComments(v, commit) {
            this.harvestPlan.SafetyComments = v.safety;
            this.harvestPlan.QualityComments = v.quality;
            this.harvestPlan.GeneralComments = v.general;

            await this.updateHarvestPlanDetail();

            commit();            
        }
    }
}
</script>